// IMPORTS
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

// COMPONENTS
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import InformationBox from "../../components/InformationBox";
import PortalDropdown from "../../components/PortalDropdown";

// UTILS & SERVICES
import useLanguage from "../../utils/useLanguage";
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";
import { getBankSubcompanies } from "../../services/bank/bankIraqiCompanyServices";

// ASSETS
import PageWrapper from "../../components/PageWrapper";
import BackToDashboard from "../../components/BackToDashboard";

export default function BankIraqiCompaniesPage() {
  // HOOKS
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);
  const { loading, setLoading } = useContext(DataContext);

  // STATES - Iraqi Company Data
  const [subCompanies, setSubCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  // STATES - Search & Filter
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);

  const [selectedFilter, setSelectedFilter] = useState({
    title: "companyName",
    value: "companyName",
  });
  const [sortOrder, setSortOrder] = useState({
    title: "ascending",
    value: "asc",
  });

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // STATES - UI Control
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
  });
  const [errorMessage, setErrorMessage] = useState("");

  // API HANDLERS
  const getSubCompanies = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const searchParams = {
        organizationName: userData?.organizationName || "",
        page: pagination.currentPage,
        // limit: pagination.itemsPerPage,
        limit: 10,
      };

      if (dateFrom) searchParams.dateFrom = dateFrom;
      if (dateTo) searchParams.dateTo = dateTo;
      if (selectedFilter.value === "companyName" && appliedSearchQuery) {
        searchParams.companyName = appliedSearchQuery;
      }

      // Add sorting for invoice-related fields
      if (selectedFilter.value === "totalInvoices") {
        searchParams.invoiceSort = sortOrder.value;
      } else if (selectedFilter.value === "completedInvoices") {
        searchParams.completedSort = sortOrder.value;
      } else if (selectedFilter.value === "uncompletedInvoices") {
        searchParams.uncompletedSort = sortOrder.value;
      } else if (selectedFilter.value === "payments") {
        searchParams.paymentSort = sortOrder.value;
      }

      const response = await getBankSubcompanies(searchParams);

      if (response.data) {
        setSubCompanies(response.data.subCompanies || []);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalRecords: response.data.pagination.totalItems,
          itemsPerPage: response.data.pagination.itemsPerPage,
        });
      }
    } catch (error) {
      console.error("Failed to get subcompanies:", error);
      setErrorMessage(
        error.response?.data?.error || "Failed to fetch subcompanies",
      );
    } finally {
      setLoading(false);
    }
  };

  // EVENT HANDLERS
  const handleFilterChange = (selected) => {
    setSelectedFilter(selected);
    setSearchQuery("");
    setAppliedSearchQuery("");
    setSortOrder({ title: "ascending", value: "asc" });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearch = () => {
    setAppliedSearchQuery(searchQuery);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setRequestVersion((v) => v + 1);
  };

  const handleSort = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setRequestVersion((v) => v + 1);
  };
  const isSortableField = selectedFilter.value !== "companyName";

  // FILTER OPTIONS
  const filterOptions = [
    { title: t("companyName"), value: "companyName" },
    { title: t("numberOfInvoices"), value: "totalInvoices" },
    { title: t("numberOfCompletedInvoices"), value: "completedInvoices" },
    { title: t("numberOfUncompletedInvoices"), value: "uncompletedInvoices" },
    { title: t("numberOfPayments"), value: "payments" },
  ];
  const sortOptions = [
    { title: t("ascending"), value: "asc" },
    { title: t("descending"), value: "desc" },
  ];

  // TABLE CONFIGURATION
  const titles = [
    { value: "companyName" },
    { value: "numberOfInvoices" },
    { value: "numberOfCompletedInvoices" },
    { value: "numberOfUncompletedInvoices" },
    { value: "numberOfPayments" },
  ];

  const rows = subCompanies?.map((item) => [
    {
      type: TABLE_CELL_TYPES.LINK,
      value: currentDirection === "rtl" ? item?.nameInAr : item?.name,
      to: `/bank-subcompanies/${item.id}`,
    },
    { value: item?.invoiceStats?.total || 0, type: TABLE_CELL_TYPES.TEXT },
    { value: item?.invoiceStats?.completed || 0, type: TABLE_CELL_TYPES.TEXT },
    {
      value: item?.invoiceStats?.uncompleted || 0,
      type: TABLE_CELL_TYPES.TEXT,
    },
    { value: item?.invoiceStats?.payments || 0, type: TABLE_CELL_TYPES.TEXT },
  ]);

  const handleEnter = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    if (selectedFilter.value === "companyName") {
      handleSearch();
      return;
    }

    handleSort();
  };

  useEffect(() => {
    if (userData?.organizationName) {
      getSubCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.currentPage,
    selectedFilter.value,
    sortOrder.value,
    dateFrom,
    dateTo,

    appliedSearchQuery,
    requestVersion,

    userData?.organizationName,
  ]);

  return (
    <PageWrapper >

      <h3>{t("iraqiCompanies")}</h3>

      <div>
        {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}
        <div className="o-column-row-switcher o-column--gap-size_300 u-margin--bottom_500 u-margin--top_500">
          <CustomInput
            name="dateFrom"
            title={t("from")}
            type="date"
            thick
            onChange={(e) => {
              setDateFrom(e?.target?.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            onKeyDown={handleEnter}
            resetSearch={() => {
              setDateFrom("");
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            value={dateFrom}
          />
          <CustomInput
            name="dateTo"
            title={t("to")}
            type="date"
            thick
            onChange={(e) => {
              setDateTo(e?.target?.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            onKeyDown={handleEnter}
            resetSearch={() => {
              setDateTo("");
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            value={dateTo}
          />
        </div>

        {/* SEARCH/SORT FUNCTIONALITY */}
        <div className="c-search-bar u-margin--bottom_500">
          <section className="c-search-bar-label-input">
            <label>
              {t("searchTitle")} {t(selectedFilter?.title)}:
            </label>

            {isSortableField ? (
              <PortalDropdown
                options={sortOptions}
                selected={sortOrder}
                setSelected={(selected) => {
                  setSortOrder(selected);
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                thick
              />
            ) : (
              <div className="u-width--min-width_230">
                <CustomInput
                  placeholder={`${t("searchTitle")} ${t(
                    selectedFilter?.value,
                  )}`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleEnter}
                  resetSearch={() => {
                    setSearchQuery("");
                    setAppliedSearchQuery("");
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    setRequestVersion((v) => v + 1);
                  }}
                  thick
                />
              </div>
            )}
          </section>

          <div className="o-row o-row--gap-size_400">
            <PortalDropdown
              options={filterOptions}
              selected={selectedFilter}
              setSelected={handleFilterChange}
              thick
            />
            <CustomButton
              title={t("search")}
              onClick={isSortableField ? handleSort : handleSearch}
              icon="search"
              iconSize={20}
            />
          </div>
        </div>

        {/* SUBCOMPANIES TABLE */}
        {loading ? (
          <Loader />
        ) : subCompanies.length === 0 ? (
          <InformationBox>
            <p>{t("noIraqiCompanies")}</p>
          </InformationBox>
        ) : (
          <ul className="users-list">
            <RoundedTable
              titles={titles}
              rows={rows}
              fullData={subCompanies}
              pagination={pagination}
              setPagination={setPagination}
              selectedRows={selectedCompanies}
              setSelectedRows={setSelectedCompanies}
            />
          </ul>
        )}
      </div>
    </PageWrapper>
  );
}
